import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import type { TFunction } from 'i18next';
import useContractDraftSource, {
  type ContractDraftSourceErrorKind,
} from '@/hooks/approval/useContractDraftSource';
import useProjectContractDraft from '@/hooks/approval/useProjectContractDraft';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import { IS_CONTRACT_AI_DRAFT_ENABLED } from '@/constant';
import { isContractIdsWithinLimit } from '@/utils/approval/contractIds';
import { contractDraftEditorApiAtom } from '../atoms';
import type { ApprovalFormValues } from '../types';
import { indexContractsById } from './contractDisplay';

export const describeDraftSourceError = (
  kind: ContractDraftSourceErrorKind | undefined,
  t: TFunction,
): string => {
  switch (kind) {
    case 'elaw-unavailable':
      return t('법틀 연동 장애로 계약서 목록을 불러오지 못했습니다.');
    case 'invalid-request':
      return t('작성자 사번 정보가 없어 계약서를 조회할 수 없습니다.');
    case 'forbidden-contract':
      return t('접근 권한이 없는 계약서가 포함되어 있습니다.');
    case 'unknown':
      return t('계약서 목록을 불러오지 못했습니다.');
    default:
      // 재조회(카드 복원) 실패는 조회 슬롯과 같은 문구
      return t('계약 정보를 불러오지 못했습니다.');
  }
};

const EMPTY_IDS: number[] = [];

/**
 * 연결 계약 상태 — 진실원은 RHF contractIds(순서 = 묶음 순서).
 * 카드 메타는 두 응답을 합쳐 찾음: 편집·법틀 진입분은 draft-source?contractIds[](순서·최신 accessLink),
 * 목록에서 고른 건은 전건 응답. initialContractIds 는 폼이 비어 있을 때만 1회 채움 —
 * 저장 시점에 이미 들어가 있어야 해서 재조회 성공을 기다리지 않음
 */
type Options = {
  /** initialContractIds 재조회가 404(접근 불가)면 그 ID 를 폼에서 빼고 스낵바 안내. 법틀 진입 전용 */
  discardInitialOnForbidden?: boolean;
  /** 신규 작성(법틀 진입)만 true — 들고 온 계약의 AI 초안을 바로 생성. 편집은 본문에 이미 있음 */
  generateInitialDrafts?: boolean;
};

const useContractLinkField = (
  apvType: number,
  initialContractIds?: number[],
  {
    discardInitialOnForbidden = false,
    generateInitialDrafts = false,
  }: Options = {},
) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { setValue, getValues } = useFormContext<ApprovalFormValues>();
  const contractIds = useWatch<ApprovalFormValues, 'contractIds'>({
    name: 'contractIds',
  });
  const ids = contractIds ?? EMPTY_IDS;

  // 부모 렌더마다 새 배열이 와도 내용이 같으면 재실행 안 되게 key 문자열로 동일성 판정
  const initialKey = (initialContractIds ?? EMPTY_IDS).join(',');
  const initialIds = useMemo(
    () => (initialKey ? initialKey.split(',').map(Number) : EMPTY_IDS),
    [initialKey],
  );
  const available = useContractDraftSource(apvType);
  const restored = useContractDraftSource(apvType, initialIds);
  // 신규/갱신은 계약 ID 를 지정한 조회에만 옴 — 등록한 계약 전체를 다시 받아 목록 값보다 앞에 둠
  const detailed = useContractDraftSource(apvType, ids);

  const contractsById = useMemo(
    () =>
      indexContractsById([
        detailed.contracts,
        restored.contracts,
        available.contracts,
      ]),
    [detailed.contracts, restored.contracts, available.contracts],
  );

  const contractLabel = t('계약서');
  const resolveTitle = useCallback(
    (contractId: number) =>
      contractsById.get(contractId)?.contractName?.trim() ||
      `${contractLabel} ${contractId}`,
    [contractsById, contractLabel],
  );
  const draft = useProjectContractDraft({
    enabled: IS_CONTRACT_AI_DRAFT_ENABLED,
    passportId: useAtomValue(MyInfoState)?.passportId,
    resolveTitle,
    editorApi: useAtomValue(contractDraftEditorApiAtom),
  });
  const requestDraft = draft.request;
  const discardDraft = draft.discard;

  // 404 는 재시도해도 같은 확정 응답 — 1회만 처리. 그 사이 사용자가 직접 등록한 계약은 남김
  const forbiddenHandledRef = useRef(false);
  useEffect(() => {
    if (!discardInitialOnForbidden) return;
    if (restored.errorKind !== 'forbidden-contract') return;
    if (forbiddenHandledRef.current) return;
    forbiddenHandledRef.current = true;
    const current = getValues('contractIds') ?? EMPTY_IDS;
    setValue(
      'contractIds',
      current.filter((id) => !initialIds.includes(id)),
    );
    initialIds.forEach(discardDraft);
    snackbar.showError({
      message: t(
        '접근 권한이 없는 계약서라 등록하지 못했습니다. 계약서를 다시 선택해 주세요.',
      ),
    });
  }, [
    discardInitialOnForbidden,
    restored.errorKind,
    initialIds,
    getValues,
    setValue,
    snackbar,
    t,
    discardDraft,
  ]);

  useEffect(() => {
    if (!initialIds.length) return;
    if (getValues('contractIds')?.length) return;
    setValue('contractIds', initialIds);
  }, [initialIds, getValues, setValue]);

  // 법틀 진입(신규)은 레거시대로 계약 정보를 받은 뒤 초안 생성 — 초안 제목에 계약명이 들어감.
  // 계약 정보 조회가 실패하면 레거시처럼 생성 안 함. 편집 진입은 본문에 이미 있어 생성 안 함
  const initialDraftsRequestedRef = useRef(false);
  const restoredContracts = restored.contracts;
  useEffect(() => {
    if (!generateInitialDrafts || initialDraftsRequestedRef.current) return;
    if (!restoredContracts?.length) return;
    initialDraftsRequestedRef.current = true;
    requestDraft(restoredContracts.map((contract) => contract.contractId));
  }, [generateInitialDrafts, restoredContracts, requestDraft]);

  const linked = useMemo(
    () =>
      ids.map((contractId) => ({
        contractId,
        contract: contractsById.get(contractId),
      })),
    [ids, contractsById],
  );

  const [pendingId, setPendingId] = useState('');

  const register = useCallback(() => {
    const id = Number(pendingId);
    if (!pendingId || !Number.isFinite(id)) {
      snackbar.showError({ message: t('추가할 계약서를 선택해 주세요.') });
      return;
    }
    const current = getValues('contractIds') ?? EMPTY_IDS;
    if (current.includes(id)) {
      snackbar.showError({ message: t('이미 등록된 계약서입니다.') });
      return;
    }
    const next = [...current, id];
    if (!isContractIdsWithinLimit(next)) {
      snackbar.showError({
        message: t('등록 가능한 계약서 수를 초과했습니다.'),
      });
      return;
    }
    setValue('contractIds', next, { shouldDirty: true });
    setPendingId('');
    requestDraft([id]);
  }, [pendingId, getValues, setValue, snackbar, t, requestDraft]);

  const remove = useCallback(
    (contractId: number) => {
      const current = getValues('contractIds') ?? EMPTY_IDS;
      setValue(
        'contractIds',
        current.filter((id) => id !== contractId),
        { shouldDirty: true },
      );
      discardDraft(contractId);
    },
    [getValues, setValue, discardDraft],
  );

  return {
    ids,
    linked,
    pendingId,
    setPendingId,
    register,
    remove,
    available,
    restored,
    draft: {
      statuses: draft.statuses,
      retry: draft.retry,
      resolveTitle,
    },
  };
};

export default useContractLinkField;
